import { forwardRef } from 'react';
import {
  Card as MuiCard,
  CardProps as MuiCardProps,
  CardContent as MuiCardContent,
  CardContentProps as MuiCardContentProps,
  CardActions as MuiCardActions,
  CardActionsProps as MuiCardActionsProps,
  CardHeader as MuiCardHeader,
  CardHeaderProps as MuiCardHeaderProps,
  CardMedia as MuiCardMedia,
  CardMediaProps as MuiCardMediaProps,
  CardActionArea as MuiCardActionArea,
  CardActionAreaProps as MuiCardActionAreaProps,
} from '@mui/material';

/**
 * Card component - A container for grouping related content and actions.
 *
 * Card is a surface component that extends Paper with additional semantic subcomponents
 * for structured content layout. Use Card when you need a clear visual container with
 * optional header, media, content, and action areas.
 *
 * ## When to Use Card
 *
 * **Use Card when:**
 * - You need semantic structure (header, content, actions, media)
 * - Displaying content that users can interact with (click, expand, etc.)
 * - Creating dashboard cards or content cards with consistent layout
 * - Grouping related information with optional actions
 *
 * **Don't use Card when:**
 * - Simple elevated container is enough (use Paper instead)
 * - Just need layout without elevation (use Box instead)
 * - Building custom complex components (use composition with Card subcomponents)
 *
 * ## Mobile App Usage
 *
 * Based on analysis of 3 uses in the mobile app:
 * - **Primary pattern**: Custom Widget component (abstracts Card with title/subtitle/action/footer props)
 * - **Secondary pattern**: Plain Card as form container (tab-based forms)
 * - **Layout preference**: Box-based layouts over CardContent/CardActions (more flexible)
 * - **Theme**: Sharp corners (borderRadius=0 in theme)
 *
 * ## Component Family
 *
 * Card comes with several subcomponents for structured layout:
 * - **Card**: Main container with elevation or outlined variant
 * - **CardHeader**: Title, subtitle, avatar, and action button area
 * - **CardContent**: Main content area with default padding
 * - **CardActions**: Action buttons area (typically at bottom)
 * - **CardMedia**: Media area for images, video, or other media
 * - **CardActionArea**: Makes entire card or section clickable
 *
 * ## Variants
 *
 * - **elevation** (default): Uses shadow to create depth (inherits Paper elevation system)
 * - **outlined**: Uses border instead of shadow (lighter visual weight)
 * - **raised**: Legacy prop - applies elevation={8} for stronger shadow
 *
 * @example
 * // Simple card with content only
 * <Card>
 *   <CardContent>
 *     <Typography variant="h5">Card Title</Typography>
 *     <Typography variant="body2">Card content goes here</Typography>
 *   </CardContent>
 * </Card>
 *
 * @example
 * // Card with header, content, and actions
 * <Card>
 *   <CardHeader
 *     title="Card Title"
 *     subheader="Card Subtitle"
 *     action={<IconButton><MoreVertIcon /></IconButton>}
 *   />
 *   <CardContent>
 *     <Typography>Main content area with padding</Typography>
 *   </CardContent>
 *   <CardActions>
 *     <Button size="small">Cancel</Button>
 *     <Button size="small" variant="contained">Confirm</Button>
 *   </CardActions>
 * </Card>
 *
 * @example
 * // Card with media (image card)
 * <Card>
 *   <CardMedia
 *     component="img"
 *     height="140"
 *     image="/path/to/image.jpg"
 *     alt="Description"
 *   />
 *   <CardContent>
 *     <Typography variant="h5">Image Card</Typography>
 *     <Typography variant="body2">Description below image</Typography>
 *   </CardContent>
 *   <CardActions>
 *     <Button size="small">Share</Button>
 *     <Button size="small">Learn More</Button>
 *   </CardActions>
 * </Card>
 *
 * @example
 * // Interactive clickable card
 * <Card>
 *   <CardActionArea onClick={() => handleCardClick()}>
 *     <CardContent>
 *       <Typography variant="h5">Clickable Card</Typography>
 *       <Typography variant="body2">Click anywhere on this card</Typography>
 *     </CardContent>
 *   </CardActionArea>
 *   <CardActions>
 *     <Button size="small">Action</Button>
 *   </CardActions>
 * </Card>
 *
 * @example
 * // Full composition with all subcomponents
 * <Card>
 *   <CardHeader
 *     avatar={<Avatar>R</Avatar>}
 *     title="Recipe Title"
 *     subheader="September 14, 2024"
 *     action={<IconButton><FavoriteIcon /></IconButton>}
 *   />
 *   <CardMedia
 *     component="img"
 *     height="194"
 *     image="/recipe.jpg"
 *     alt="Recipe"
 *   />
 *   <CardContent>
 *     <Typography>Detailed recipe instructions and description</Typography>
 *   </CardContent>
 *   <CardActions disableSpacing>
 *     <Button>Share</Button>
 *     <Button>Save</Button>
 *   </CardActions>
 * </Card>
 *
 * @example
 * // Widget-style pattern (mobile app pattern)
 * <Card variant="outlined" sx={{ boxShadow: 1 }}>
 *   <Box sx={{ p: 2, display: 'flex', justifyContent: 'space-between' }}>
 *     <Box>
 *       <Typography variant="h6">Widget Title</Typography>
 *       <Typography variant="body2" color="text.secondary">Widget Subtitle</Typography>
 *     </Box>
 *     <IconButton><MoreVertIcon /></IconButton>
 *   </Box>
 *   <Divider />
 *   <CardContent>
 *     <Typography>Widget content here</Typography>
 *   </CardContent>
 *   <Divider />
 *   <Box sx={{ p: 2, borderTop: 1, borderColor: 'divider' }}>
 *     <Button>Footer Action</Button>
 *   </Box>
 * </Card>
 */
export type CardProps = MuiCardProps;

/**
 * Card component wrapper around MUI Card.
 */
export const Card = forwardRef<HTMLDivElement, CardProps>((props, ref) => (
  <MuiCard ref={ref} {...props} />
));

Card.displayName = 'Card';

/**
 * CardContent component - Main content area of the card with default padding.
 *
 * CardContent applies consistent padding and is typically used to contain the main
 * text content, images, or other elements within a Card.
 *
 * @example
 * <Card>
 *   <CardContent>
 *     <Typography variant="h5">Title</Typography>
 *     <Typography variant="body2">Content text</Typography>
 *   </CardContent>
 * </Card>
 */
export type CardContentProps = MuiCardContentProps;

/**
 * CardContent component wrapper around MUI CardContent.
 */
export const CardContent = forwardRef<HTMLDivElement, CardContentProps>((props, ref) => (
  <MuiCardContent ref={ref} {...props} />
));

CardContent.displayName = 'CardContent';

/**
 * CardActions component - Container for action buttons at the bottom of the card.
 *
 * CardActions applies consistent spacing and alignment for buttons. By default,
 * buttons have spacing between them. Use `disableSpacing` to remove spacing.
 *
 * @example
 * <Card>
 *   <CardContent>...</CardContent>
 *   <CardActions>
 *     <Button size="small">Cancel</Button>
 *     <Button size="small" variant="contained">Confirm</Button>
 *   </CardActions>
 * </Card>
 *
 * @example
 * // Actions with no spacing
 * <CardActions disableSpacing>
 *   <IconButton><ShareIcon /></IconButton>
 *   <IconButton><FavoriteIcon /></IconButton>
 * </CardActions>
 */
export type CardActionsProps = MuiCardActionsProps;

/**
 * CardActions component wrapper around MUI CardActions.
 */
export const CardActions = forwardRef<HTMLDivElement, CardActionsProps>((props, ref) => (
  <MuiCardActions ref={ref} {...props} />
));

CardActions.displayName = 'CardActions';

/**
 * CardHeader component - Header section with title, subtitle, avatar, and action.
 *
 * CardHeader provides a standardized layout for card headers with optional avatar
 * on the left, title and subheader in the center, and action button on the right.
 *
 * ## Props
 * - **title**: Main heading text (can be string or ReactNode)
 * - **subheader**: Secondary text below title (can be string or ReactNode)
 * - **avatar**: Left-aligned avatar (typically Avatar component or icon)
 * - **action**: Right-aligned action button (typically IconButton)
 * - **titleTypographyProps**: Props to customize title Typography
 * - **subheaderTypographyProps**: Props to customize subheader Typography
 *
 * @example
 * <Card>
 *   <CardHeader
 *     title="Recipe Title"
 *     subheader="September 14, 2024"
 *   />
 *   <CardContent>...</CardContent>
 * </Card>
 *
 * @example
 * // With avatar and action
 * <CardHeader
 *   avatar={<Avatar>R</Avatar>}
 *   action={<IconButton><MoreVertIcon /></IconButton>}
 *   title="User Name"
 *   subheader="5 minutes ago"
 * />
 *
 * @example
 * // Custom typography
 * <CardHeader
 *   title="Custom Title"
 *   subheader="Custom Subtitle"
 *   titleTypographyProps={{ variant: 'h4', color: 'primary' }}
 *   subheaderTypographyProps={{ variant: 'caption' }}
 * />
 */
export type CardHeaderProps = MuiCardHeaderProps;

/**
 * CardHeader component wrapper around MUI CardHeader.
 */
export const CardHeader = forwardRef<HTMLDivElement, CardHeaderProps>((props, ref) => (
  <MuiCardHeader ref={ref} {...props} />
));

CardHeader.displayName = 'CardHeader';

/**
 * CardMedia component - Media container for images, video, or other media.
 *
 * CardMedia renders media content (images, video, etc.) with consistent styling.
 * Commonly used with `component="img"` for images or `component="video"` for videos.
 *
 * ## Props
 * - **component**: HTML element to render ('img', 'video', 'picture', etc.)
 * - **image**: Background image URL (when not using component prop)
 * - **src**: Source URL (when using component="img" or component="video")
 * - **height**: Fixed height for the media element
 * - **alt**: Alt text for images (accessibility)
 *
 * @example
 * // Image as component
 * <Card>
 *   <CardMedia
 *     component="img"
 *     height="140"
 *     image="/path/to/image.jpg"
 *     alt="Description"
 *   />
 *   <CardContent>...</CardContent>
 * </Card>
 *
 * @example
 * // Background image
 * <CardMedia
 *   image="/path/to/image.jpg"
 *   sx={{ height: 140 }}
 * />
 *
 * @example
 * // Video
 * <CardMedia
 *   component="video"
 *   height="194"
 *   src="/path/to/video.mp4"
 *   controls
 * />
 */
export type CardMediaProps = MuiCardMediaProps;

/**
 * CardMedia component wrapper around MUI CardMedia.
 */
export const CardMedia = forwardRef<HTMLDivElement, CardMediaProps>((props, ref) => (
  <MuiCardMedia ref={ref} {...props} />
));

CardMedia.displayName = 'CardMedia';

/**
 * CardActionArea component - Makes card or section clickable with hover effects.
 *
 * CardActionArea provides hover effects and click handling for interactive cards.
 * Wrap card content in CardActionArea to make the entire card or a section clickable.
 * Renders as a button element for proper accessibility.
 *
 * ## Usage Notes
 * - Use when entire card should be clickable
 * - Provides visual feedback on hover (ripple effect, slight elevation increase)
 * - Automatically handles keyboard navigation (Enter/Space to activate)
 * - Can be combined with CardActions for primary click + additional actions
 *
 * @example
 * // Clickable card
 * <Card>
 *   <CardActionArea onClick={() => navigate('/details')}>
 *     <CardMedia
 *       component="img"
 *       height="140"
 *       image="/image.jpg"
 *       alt="Item"
 *     />
 *     <CardContent>
 *       <Typography variant="h5">Item Title</Typography>
 *       <Typography variant="body2">Click to view details</Typography>
 *     </CardContent>
 *   </CardActionArea>
 * </Card>
 *
 * @example
 * // Clickable content with separate actions
 * <Card>
 *   <CardActionArea onClick={() => navigate('/item')}>
 *     <CardContent>
 *       <Typography variant="h5">Item</Typography>
 *       <Typography variant="body2">Click card to open</Typography>
 *     </CardContent>
 *   </CardActionArea>
 *   <CardActions>
 *     <Button onClick={(e) => { e.stopPropagation(); handleShare(); }}>
 *       Share
 *     </Button>
 *   </CardActions>
 * </Card>
 */
export type CardActionAreaProps = MuiCardActionAreaProps;

/**
 * CardActionArea component wrapper around MUI CardActionArea.
 */
export const CardActionArea = forwardRef<HTMLButtonElement, CardActionAreaProps>((props, ref) => (
  <MuiCardActionArea ref={ref} {...props} />
));

CardActionArea.displayName = 'CardActionArea';
